/**
 * CPC-205–209: frozen stage identities and completeness contracts. Understand
 * and Plan remain readable when continuity is red. Write stages still wait for
 * no-loss, conflict, and history checks.
 */

import type { CarePlanContinuityDecision } from './care-plan-continuity';

export const CARE_PLAN_STAGE_IDS = [
  'understand',
  'plan',
  'services-funding',
  'agree',
  'activate',
] as const;

export type CarePlanStageId = (typeof CARE_PLAN_STAGE_IDS)[number];

export const CARE_PLAN_STAGE_ALIASES: Record<string, CarePlanStageId> = {
  understand: 'understand',
  plan: 'plan',
  'services-funding': 'services-funding',
  services_funding: 'services-funding',
  agree: 'agree',
  activate: 'activate',
  activate_handoff: 'activate',
  'activate-handoff': 'activate',
};

export const CARE_PLAN_STAGE_LABELS: Record<CarePlanStageId, string> = {
  understand: 'Understand',
  plan: 'Plan',
  'services-funding': 'Services & funding',
  agree: 'Agree',
  activate: 'Activate',
};

const READABLE_WHEN_CONTINUITY_RED: ReadonlySet<CarePlanStageId> = new Set(['understand', 'plan']);

export const CARE_PLAN_STAGE_BLOCKS: Record<CarePlanStageId, readonly string[]> = {
  understand: [
    'participation-authority',
    'assessments',
    'evidence-inbox',
    'provenance',
    'proposals',
  ],
  plan: ['goals', 'risks', 'interventions', 'preferences', 'worker-instructions'],
  'services-funding': [
    'inclusions',
    'cadence',
    'provider-rate',
    'budget',
    'means-hardship-contribution',
  ],
  agree: [
    'service-agreement',
    'individualised-budget',
    'participant-copy',
    'acknowledgement',
    'consents',
  ],
  activate: ['server-blockers', 'approval', 'seven-outputs'],
};

export const CARE_PLAN_BLOCK_LABELS: Record<string, string> = {
  'participation-authority': 'Who can decide',
  assessments: 'Assessments',
  'evidence-inbox': 'Import inbox',
  provenance: 'Provenance',
  proposals: 'Proposals',
  goals: 'Goals',
  risks: 'Risks',
  interventions: 'Interventions',
  preferences: 'Preferences',
  'worker-instructions': 'Worker must-dos',
  inclusions: 'Included services',
  cadence: 'Cadence',
  'provider-rate': 'Provider rate',
  budget: 'Plan budget',
  'means-hardship-contribution': 'Means, hardship and contribution',
  'service-agreement': 'Service agreement',
  'individualised-budget': 'Individualised budget',
  'participant-copy': 'Participant copy',
  acknowledgement: 'Acknowledgement',
  consents: 'Consents',
  'server-blockers': 'Activation blockers',
  approval: 'Approval',
  'seven-outputs': 'Activation outputs',
};

export type CarePlanStageStartDecision =
  | { allowed: true; stage: CarePlanStageId }
  | {
      allowed: false;
      stage: CarePlanStageId | null;
      reason: 'unknown-stage' | 'continuity-not-green';
    };

export function isCarePlanStageId(value: string): value is CarePlanStageId {
  return (CARE_PLAN_STAGE_IDS as readonly string[]).includes(value);
}

export function resolveCarePlanStageId(value: string): CarePlanStageId | null {
  return CARE_PLAN_STAGE_ALIASES[value] ?? (isCarePlanStageId(value) ? value : null);
}

export function decideCarePlanStageStart(
  stage: string,
  continuity: CarePlanContinuityDecision,
): CarePlanStageStartDecision {
  const resolved = resolveCarePlanStageId(stage);
  if (!resolved) {
    return { allowed: false, stage: null, reason: 'unknown-stage' };
  }
  if (READABLE_WHEN_CONTINUITY_RED.has(resolved) && continuity.mayReadDraftStage) {
    return { allowed: true, stage: resolved };
  }
  if (
    !continuity.mayStartStage ||
    !continuity.noLoss ||
    !continuity.conflictClear ||
    !continuity.historyGreen
  ) {
    return { allowed: false, stage: resolved, reason: 'continuity-not-green' };
  }
  return { allowed: true, stage: resolved };
}

export function selectMountedCarePlanStage(
  requested: string | null,
  continuity: CarePlanContinuityDecision,
): CarePlanStageId | null {
  if (!requested) return null;
  const decision = decideCarePlanStageStart(requested, continuity);
  return decision.allowed ? decision.stage : null;
}

export function carePlanStageCompleteness(
  stage: CarePlanStageId,
  completedBlocks: readonly string[],
): { complete: boolean; missing: readonly string[] } {
  const required = CARE_PLAN_STAGE_BLOCKS[stage];
  const done = new Set(completedBlocks);
  const missing = required.filter((block) => !done.has(block));
  return { complete: missing.length === 0, missing };
}
